import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "shared/context/auth-context";
import { trackSuiteEvent } from "shared/util/track";
import "./DemoOptimizeCoach.css";

const SELECTOR = "[data-demo-coach='optimize']";
const seenKeyFor = (id) => (id ? `optimize_coach_seen:${id}` : null);

/**
 * "Optimize Route" butonuna işaret eden coachmark.
 *
 * - Sadece DemoLeadCaptureProvider içinde (demo hesabı) render edilir.
 * - HESABA bağlı bayrak (`optimize_coach_seen:${auth.id}`) ile YALNIZCA İLK KEZ gösterilir;
 *   her sayfa/servis açılışında tekrar çıkmaz. Yeni demo açılınca sıfırlanır.
 * - Butona ya da alttaki yönlendirme kutusuna basınca gerçek Optimize çalışır ve kutu kapanır.
 * - Tamamen overlay'dir; butonu sarmalayan `[data-demo-coach="optimize"]` elemanına
 *   konumlanır. Halka pointer-events:none → tıklamayı engellemez.
 */
const DemoOptimizeCoach = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const seenKey = seenKeyFor(auth?.id);

  const readSeen = (key) => {
    try {
      return key ? localStorage.getItem(key) === "1" : false;
    } catch {
      return false;
    }
  };

  const [dismissed, setDismissed] = useState(() => readSeen(seenKey));
  const [rect, setRect] = useState(null);
  const dismissedRef = useRef(dismissed);
  dismissedRef.current = dismissed;
  const shownTrackedRef = useRef(false);

  // Hesap değişince (yeni demo) bayrağı yeniden oku.
  useEffect(() => {
    setDismissed(readSeen(seenKey));
    shownTrackedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seenKey]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      if (seenKey) localStorage.setItem(seenKey, "1");
    } catch {
      /* yoksay */
    }
  }, [seenKey]);

  const runOptimize = useCallback(() => {
    const root = document.querySelector(SELECTOR);
    const button = root?.querySelector("button");
    if (!button || button.disabled) return;
    button.click();
  }, []);

  useEffect(() => {
    if (dismissed) return undefined;

    let attachedEl = null;

    const onBtnClick = () => {
      trackSuiteEvent("Demo — Optimize Pressed", "button_click", { key: "first_action_completed" });
      dismiss();
    };

    const update = () => {
      if (dismissedRef.current) return;
      const el = document.querySelector(SELECTOR);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      // Görünür değilse (panel kapalı vs.) konumlandırma yapma.
      if (r.width === 0 || r.height === 0) {
        setRect(null);
        return;
      }
      setRect({ top: r.top, left: r.left, right: r.right, width: r.width, height: r.height });

      if (attachedEl !== el) {
        if (attachedEl) attachedEl.removeEventListener("click", onBtnClick);
        el.addEventListener("click", onBtnClick);
        attachedEl = el;
      }
      if (!shownTrackedRef.current) {
        shownTrackedRef.current = true;
        trackSuiteEvent("Demo — Optimize Coach Shown", "other", { key: "optimize_coach_shown" });
      }
    };

    // Buton async geldiği için periyodik kontrol + scroll/resize takibi.
    const interval = setInterval(update, 400);
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);

    return () => {
      clearInterval(interval);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      if (attachedEl) attachedEl.removeEventListener("click", onBtnClick);
    };
  }, [dismissed, dismiss]);

  if (dismissed || !rect) return null;

  return (
    <>
      <div
        className="doc-shade"
        aria-hidden="true"
        style={{
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
        }}
      />
      <div
        className="doc-ring"
        style={{
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
        }}
      />
      <button
        type="button"
        className="doc-callout"
        style={{
          top: rect.top + rect.height + 14,
          right: Math.max(12, window.innerWidth - rect.right),
        }}
        onClick={runOptimize}
      >
        <div className="doc-callout__arrow" />
        <div className="doc-callout__text">
          <strong>{t("optimizeCoach")}</strong>
          <p className="doc-callout__detail">{t("optimizeCoachDetail")}</p>
        </div>
      </button>
    </>
  );
};

export default DemoOptimizeCoach;
